import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import type { ThemeClasses, Brand, BrandFormData } from "../types";
import Modal from "../../../../../../components/base/Modal";
import { getCustomSelectStyles } from "../../../../shared/index";

interface ModelEditModalProps {
  brand: Brand;
  editForm: Partial<BrandFormData>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSave: (data: Partial<BrandFormData>) => void;
}

export function ModelEditModal({
  brand,
  editForm,
  themeClasses,
  isDarkMode,
  isSubmitting,
  onClose,
  onSave,
}: ModelEditModalProps) {
  const { t } = useTranslation("resellerAdmin");
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<Partial<BrandFormData>>({
    defaultValues: editForm,
  });

  useEffect(() => {
    reset(editForm);
  }, [editForm, reset]);

  const getInputClassName = (hasError: boolean) =>
    `w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${
      hasError ? "border-red-500" : themeClasses.cardBorder
    } rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 ${
      hasError ? "focus:ring-red-500" : "focus:ring-primary-500"
    }`;

  const errorClassName = "text-red-500 text-xs mt-1";

  const protocolOptions = [
    { value: "TCP", label: "TCP" },
    { value: "UDP", label: "UDP" },
  ];

  const originOptions = [
    { value: "v2", label: "V2" },
    { value: "v4", label: "V4" },
    { value: "v5", label: "V5" },
  ];

  const onSubmit = (data: Partial<BrandFormData>) => {
    onSave(data);
  };

  return (
    <Modal
      width="w-full max-w-2xl"
      content={
        <form
          id="model-edit-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 w-full"
        >
          {/* Basic Info */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("models.sections.basicInfo")}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Brand */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("models.fields.brand")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={getInputClassName(!!errors.brand)}
                placeholder={t("models.placeholders.brand")}
                {...register("brand", {
                  required: t("models.validation.brandRequired"),
                })}
              />
              {errors.brand && (
                <span className={errorClassName}>{errors.brand.message}</span>
              )}
            </div>

            {/* Model */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("models.fields.model")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={getInputClassName(!!errors.model)}
                placeholder={t("models.placeholders.model")}
                {...register("model", {
                  required: t("models.validation.modelRequired"),
                })}
              />
              {errors.model && (
                <span className={errorClassName}>{errors.model.message}</span>
              )}
            </div>

            {/* Operational Alias */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("models.fields.operationalAlias")}
              </label>
              <input
                type="text"
                className={getInputClassName(false)}
                placeholder={t("models.placeholders.operationalAlias")}
                {...register("operational_alias")}
              />
            </div>

            {/* Origin (Read-only) */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("models.fields.origin")}
              </label>
              <Controller
                name="origin"
                control={control}
                render={({ field }) => (
                  <Select
                    options={originOptions}
                    styles={getCustomSelectStyles(isDarkMode, false)}
                    placeholder={t("models.placeholders.selectOrigin")}
                    value={originOptions.find((opt) => opt.value === field.value) || null}
                    isDisabled={true}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
              />
              <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                {t("models.fields.originNotEditable")}
              </p>
            </div>
          </div>

          {/* Network Info */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4 mt-6">
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("models.sections.networkInfo")}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* IP Address */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("models.fields.ipAddress")}
              </label>
              <input
                type="text"
                className={getInputClassName(false)}
                placeholder={t("models.placeholders.ipAddress")}
                {...register("network.ip_address")}
              />
            </div>

            {/* Port */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("models.fields.port")}
              </label>
              <input
                type="text"
                className={getInputClassName(false)}
                placeholder={t("models.placeholders.port")}
                {...register("network.port")}
              />
            </div>

            {/* Protocol */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("models.fields.protocol")}
              </label>
              <Controller
                name="network.protocol"
                control={control}
                render={({ field }) => (
                  <Select
                    options={protocolOptions}
                    styles={getCustomSelectStyles(isDarkMode, false)}
                    placeholder={t("models.placeholders.selectProtocol")}
                    value={protocolOptions.find((opt) => opt.value === field.value) || null}
                    onChange={(option: any) => field.onChange(option?.value || "")}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
              />
            </div>
          </div>
        </form>
      }
      themeClasses={themeClasses}
      onClose={onClose}
      title={`${t("models.modal.editTitle")} - ${brand.brand} ${brand.model}`}
      buttonAction={
        <button
          type="submit"
          form="model-edit-form"
          disabled={isSubmitting}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {isSubmitting ? t("models.actions.saving") : t("models.actions.saveChanges")}
        </button>
      }
    />
  );
}
